import React, { useEffect, useRef } from 'react';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as Sentry from '@sentry/react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import useDateFormat from '../../../hooks/useDateFormat';
import {
  ManagedLead,
  ManagedLeadProductPath,
} from '../../../services/api/managed-leads/managedLeads';
import { MANAGED_LEAD_PENDING_DROP_LABEL } from '../managedLeadDropFeedback';
import {
  formatManagedLeadCurrency,
  formatManagedLeadDate,
  formatManagedLeadStageAlert,
  formatManagedLeadValue,
  EMPTY_MANAGED_LEAD_VALUE,
  ManagedLeadDisplayValue,
} from '../managedLeadFormatting';
import {
  ManagedLeadUpdateRequestButton,
  useCanRequestManagedLeadUpdate,
} from './ManagedLeadUpdateRequestButton';

const EMPTY_LIST_CARD_VALUE = 'N/A';

const formatListCardValue = (value: string) =>
  value === EMPTY_MANAGED_LEAD_VALUE ? EMPTY_LIST_CARD_VALUE : value;

interface LeadDatumProps {
  label: string;
  value: string;
  containerSx?: SxProps<Theme>;
}

// Mirrors the Mutui list card datum (LabelValueItem) so every managed list
// renders labels and values with the same weight and size.
const LeadDatum = ({ label, value, containerSx = {} }: LeadDatumProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      ...containerSx,
    }}
  >
    <Typography component="dt" sx={{ fontWeight: 'bold' }}>
      {label}
    </Typography>
    <Typography
      component="dd"
      m={0}
      sx={{ fontSize: '18px', lineHeight: '22px', overflowWrap: 'anywhere' }}
    >
      {value}
    </Typography>
  </Box>
);

interface ManagedLeadCardProps {
  lead: ManagedLead;
  productDatum: {
    label: string;
    value: ManagedLeadDisplayValue;
  };
  detailBasePath: string;
  returnTo: string;
  showNeedsUpdate?: boolean;
  /**
   * Product of the single Richiesta di aggiornamento; absent where the list
   * offers none. The button is shown to admins only.
   */
  updateRequestProduct?: ManagedLeadProductPath;
}

const ManagedLeadCard = ({
  lead,
  productDatum,
  detailBasePath,
  returnTo,
  showNeedsUpdate = false,
  updateRequestProduct,
}: ManagedLeadCardProps) => {
  const canRequestUpdate = useCanRequestManagedLeadUpdate();
  const location = useLocation();
  const cardRef = useRef<HTMLElement | null>(null);
  const hasReportedInconsistency = useRef(false);
  const { formatDate } = useDateFormat();
  const cardAnchor = `managed-lead-${encodeURIComponent(lead.id)}`;
  const showInactivity = showNeedsUpdate && lead.needsUpdate;
  const inactivityContractIsInconsistent =
    showInactivity && lead.daysSinceLastActivity == null;

  useEffect(() => {
    if (location.hash === `#${cardAnchor}`) {
      cardRef.current?.focus({ preventScroll: true });
      cardRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [cardAnchor, location.hash]);

  useEffect(() => {
    if (!inactivityContractIsInconsistent) {
      hasReportedInconsistency.current = false;
      return;
    }
    if (hasReportedInconsistency.current) return;
    hasReportedInconsistency.current = true;

    Sentry.captureMessage('Managed lead inactivity contract is inconsistent', {
      level: 'error',
      extra: { leadId: lead.id },
    });
  }, [inactivityContractIsInconsistent, lead.id]);

  const inactivityMessage = inactivityContractIsInconsistent
    ? 'Lead da aggiornare'
    : `Sono passati ${lead.daysSinceLastActivity} giorni dall'ultima attività`;

  return (
    <Card
      component="article"
      elevation={0}
      id={cardAnchor}
      ref={cardRef}
      tabIndex={-1}
      sx={{ borderRadius: 0, bgcolor: 'common.white' }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap-reverse"
          gap={2}
          borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
          width="100%"
          pb={1}
        >
          <Typography
            fontWeight="bold"
            textTransform="uppercase"
            color="secondary.dark"
          >
            {lead.stage.label}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            minWidth={0}
          >
            {showInactivity && (
              <Box display="flex" alignItems="center" minWidth={0}>
                <AccessAlarmIcon fontSize="medium" color="error" />
                <Typography
                  pl={1}
                  color="error"
                  fontWeight="bold"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {inactivityMessage}
                </Typography>
              </Box>
            )}
            {lead.pendingDrop && (
              <Box display="flex" alignItems="center">
                <WarningAmberIcon fontSize="medium" color="warning" />
                <Typography pl={1} color="warning.dark" fontWeight="bold">
                  {MANAGED_LEAD_PENDING_DROP_LABEL}
                </Typography>
              </Box>
            )}
            {updateRequestProduct && canRequestUpdate && (
              <ManagedLeadUpdateRequestButton
                product={updateRequestProduct}
                dealId={lead.id}
              />
            )}
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          pt={1}
        >
          <Box minWidth={0}>
            <Typography
              component="h2"
              variant="h5"
              fontWeight="bold"
              color="secondary.dark"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {formatListCardValue(formatManagedLeadValue(lead.dealName))}
            </Typography>
            <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
              {formatListCardValue(formatManagedLeadValue(lead.phone))}
            </Typography>
          </Box>
          <Box component="dl" m={0}>
            <LeadDatum
              containerSx={{ alignItems: { sm: 'flex-end' } }}
              label="Ultima modifica"
              value={formatListCardValue(
                formatManagedLeadDate(lead.lastModifiedAt, formatDate)
              )}
            />
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap-reverse"
          gap={2}
          mt={2}
        >
          <Box component="dl" display="flex" flexWrap="wrap" gap={4} m={0}>
            <LeadDatum
              label="Importo"
              value={formatListCardValue(
                formatManagedLeadCurrency(lead.amount)
              )}
            />
            <LeadDatum
              label={productDatum.label}
              value={formatListCardValue(
                formatManagedLeadValue(productDatum.value)
              )}
            />
          </Box>
          <Box component="dl" m={0}>
            <LeadDatum
              containerSx={{ alignItems: { sm: 'flex-end' } }}
              label="Data creazione"
              value={formatListCardValue(
                formatManagedLeadDate(lead.createdAt, formatDate)
              )}
            />
          </Box>
        </Box>

        {lead.stage.alert && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {formatManagedLeadStageAlert(lead.stage.alert)}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            maxWidth: '400px',
            alignSelf: 'flex-end',
            mt: 2,
          }}
        >
          <Button
            component={RouterLink}
            to={`${detailBasePath}/${encodeURIComponent(
              lead.id
            )}?returnTo=${encodeURIComponent(returnTo)}`}
            disableElevation
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              width: '100%',
              maxWidth: '400px',
              bgcolor: (theme) =>
                showInactivity
                  ? theme.palette.error.main
                  : theme.palette.primary.light,
            }}
          >
            <Typography fontWeight="bold" p={1}>
              Visualizza i dettagli della lead
            </Typography>
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ManagedLeadCard;
